import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { 
  BedDouble, Users, CalendarCheck, CalendarX, Sparkles, Wrench, 
  TrendingUp, TrendingDown, RefreshCw, DollarSign, LogIn, User,
  Clock, Phone, Mail, CreditCard, MapPin
} from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 15000;
const FINANCIAL_ROLES = ['admin', 'super_admin', 'manager', 'accountant'];

// Room Status Configuration
const ROOM_STATUS_CONFIG = {
  available: { label: 'Available', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50 dark:bg-emerald-950' },
  occupied: { label: 'Occupied', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 dark:bg-red-950' },
  dirty: { label: 'Dirty', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 dark:bg-amber-950' },
  clean: { label: 'Clean', color: 'bg-teal-500', textColor: 'text-teal-700', bgLight: 'bg-teal-50 dark:bg-teal-950' },
  out_of_service: { label: 'OOS', color: 'bg-rose-500', textColor: 'text-rose-700', bgLight: 'bg-rose-50 dark:bg-rose-950' },
  maintenance: { label: 'Maintenance', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50 dark:bg-orange-950' }
};

const DashboardPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { hotel, rooms, roomTypes, fetchRooms, hotelSettings } = useHotel();
  const [dashboard, setDashboard] = useState(null);
  const [todayArrivals, setTodayArrivals] = useState([]);
  const [todayDepartures, setTodayDepartures] = useState([]);
  const [checkedInReservations, setCheckedInReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [financialData, setFinancialData] = useState({ revenue: 0, expenses: 0, profit: 0, reservationRevenue: 0, transferRevenue: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  // Check-in Dialog State
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const canSeeFinancials = user && FINANCIAL_ROLES.includes(user.role);
  
  // Dashboard widget visibility
  const widgets = hotelSettings?.dashboard_widgets || {
    quick_stats: true,
    todays_arrivals: true,
    room_status: true,
    todays_departures: true,
    tasks_overview: true
  };

  // Room status visibility from settings
  const roomStatusSettings = hotelSettings?.room_statuses || {
    available: true,
    occupied: true,
    dirty: true,
    clean: true,
    out_of_service: true,
    maintenance: true
  };

  // Filter visible status configs based on settings
  const visibleStatusConfigs = Object.entries(ROOM_STATUS_CONFIG).filter(
    ([status]) => roomStatusSettings[status] !== false
  );

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    if (!user || !user.hotel_id) return;
    if (showRefreshing) setRefreshing(true);
    
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');
      
      const requests = [
        axios.get(`${API}/hotels/${user.hotel_id}/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/reservations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/guests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/reservations?status=checked_in`, { headers: getAuthHeaders() })
      ];
      
      if (FINANCIAL_ROLES.includes(user.role)) {
        requests.push(
          axios.get(`${API}/hotels/${user.hotel_id}/transfers`, { headers: getAuthHeaders() }),
          axios.get(`${API}/hotels/${user.hotel_id}/expenses`, { headers: getAuthHeaders() })  // Get all expenses
        );
      }
      
      const responses = await Promise.all(requests);
      const [dashResponse, reservationsResponse, guestsResponse, checkedInResponse] = responses;
      
      setDashboard(dashResponse.data);
      setGuests(guestsResponse.data);
      setCheckedInReservations(checkedInResponse.data || []);
      
      // Filter arrivals and departures for today
      const todayReservations = reservationsResponse.data;
      const arrivals = todayReservations.filter(r => 
        ['pending', 'confirmed'].includes(r.status) && 
        r.check_in_date?.startsWith(today)
      );
      const departures = todayReservations.filter(r => 
        r.status === 'checked_in' && 
        r.check_out_date?.startsWith(today)
      );
      
      setTodayArrivals(arrivals);
      setTodayDepartures(departures);
      
      // Calculate financial data based on check-ins (when guest checks in = paid)
      if (FINANCIAL_ROLES.includes(user.role) && responses.length >= 6) {
        const transfers = responses[4].data || [];
        const expenses = responses[5].data || [];
        
        // Revenue from checked-in/checked-out reservations
        const allReservations = reservationsResponse.data || [];
        const paidReservations = allReservations.filter(r => 
          ['checked_in', 'checked_out'].includes(r.status)
        );
        // Calculate revenue: use total_amount if set, otherwise rate × nights
        const reservationRevenue = paidReservations.reduce((sum, r) => {
          const rate = r.rate_per_night || 0;
          try {
            const ci = new Date(r.check_in_date);
            const co = new Date(r.check_out_date);
            // Use date-only diff to avoid time-of-day discrepancies
            const ciDate = new Date(ci.getFullYear(), ci.getMonth(), ci.getDate());
            const coDate = new Date(co.getFullYear(), co.getMonth(), co.getDate());
            const nights = Math.max(1, Math.round((coDate - ciDate) / 86400000));
            // Prefer recalculated total over stored (stored may be wrong from old bug)
            return sum + (rate > 0 ? rate * nights : (r.total_amount || 0));
          } catch {
            return sum + (r.total_amount || rate);
          }
        }, 0);
        
        // Revenue from completed transfers
        const completedTransfers = transfers.filter(t => t.status === 'completed');
        const transferRevenue = completedTransfers.reduce((sum, t) => sum + (t.price || 0), 0);
        
        const totalRevenue = reservationRevenue + transferRevenue;
        const totalExpenses = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
        
        setFinancialData({ 
          revenue: totalRevenue, 
          expenses: totalExpenses, 
          profit: totalRevenue - totalExpenses,
          reservationRevenue,
          transferRevenue
        });
      }
      
      setLastUpdated(new Date());
      if (fetchRooms) fetchRooms();
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
    setLoading(false);
    setRefreshing(false);
  }, [user, getAuthHeaders, fetchRooms]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  
  useEffect(() => {
    const intervalId = setInterval(() => fetchDashboard(false), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchDashboard]);

  const handleManualRefresh = () => fetchDashboard(true);

  const getGuestById = (guestId) => guests.find(g => g.id === guestId);
  
  const getGuestName = (guestId) => {
    const guest = getGuestById(guestId);
    return guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown Guest';
  };

  // Get guest name for an occupied room
  const getGuestForRoom = (roomId) => {
    const reservation = checkedInReservations.find(r => r.room_id === roomId);
    if (reservation) {
      const guest = getGuestById(reservation.guest_id);
      return guest ? `${guest.first_name} ${guest.last_name}` : null;
    }
    return null;
  };

  const getRoomById = (roomId) => rooms.find(r => r.id === roomId);
  
  const getRoomNumber = (roomId) => {
    const room = getRoomById(roomId);
    return room?.number || room?.room_number || 'N/A';
  };

  const getRoomTypeName = (roomId) => {
    const room = getRoomById(roomId);
    if (room) {
      const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
      return roomType?.name || '';
    }
    return '';
  };

  const openCheckInDialog = (reservation) => {
    setSelectedReservation(reservation);
    setCheckInDialogOpen(true);
  };

  const handleCheckIn = async () => {
    if (!selectedReservation) return;
    setCheckingIn(true);
    
    try {
      await axios.post(
        `${API}/hotels/${user.hotel_id}/reservations/${selectedReservation.id}/check-in`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`${getGuestName(selectedReservation.guest_id)} checked in successfully!`);
      setCheckInDialogOpen(false);
      setSelectedReservation(null);
      fetchDashboard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
    setCheckingIn(false);
  };

  const handleCheckOut = async (reservation) => {
    try {
      await axios.post(
        `${API}/hotels/${user.hotel_id}/reservations/${reservation.id}/check-out`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`${getGuestName(reservation.guest_id)} checked out successfully!`);
      fetchDashboard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const getRoomStatusCounts = () => {
    const counts = { available: 0, occupied: 0, dirty: 0, clean: 0, out_of_service: 0, maintenance: 0 };
    rooms.forEach(r => { if (counts[r.status] !== undefined) counts[r.status]++; });
    return counts;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusCounts = getRoomStatusCounts();
  const hotelName = hotel?.name || 'Your Hotel';
  const occupancyRate = dashboard?.occupancy_rate || 0;
  const inHouse = dashboard?.today?.in_house || 0;
  const pendingCleaning = dashboard?.tasks?.pending_cleaning || 0;
  const openMaintenance = dashboard?.tasks?.open_maintenance || 0;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-['Outfit'] text-foreground">Front Desk Dashboard</h1>
          <p className="text-sm text-muted-foreground">{hotelName} — Real-time overview</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-xs text-muted-foreground hidden sm:inline">Updated: {format(lastUpdated, 'HH:mm:ss')}</span>
          <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing} data-testid="refresh-dashboard-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {widgets.quick_stats !== false && (
      <div className={`grid grid-cols-2 ${canSeeFinancials ? 'sm:grid-cols-3 lg:grid-cols-5' : 'sm:grid-cols-2 lg:grid-cols-4'} gap-2 sm:gap-4`}>
        <Card className="card-interactive" data-testid="stat-card-occupancy">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Occupancy</p>
                <p className="text-xl sm:text-2xl font-bold font-['Outfit']">{occupancyRate}%</p>
              </div>
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-50 dark:bg-blue-950">
                <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="stat-card-arrivals">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Arrivals</p>
                <p className="text-xl sm:text-2xl font-bold font-['Outfit'] text-emerald-600">{todayArrivals.length}</p>
              </div>
              <div className="p-1.5 sm:p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                <CalendarCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="stat-card-departures">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Departures</p>
                <p className="text-xl sm:text-2xl font-bold font-['Outfit'] text-amber-600">{todayDepartures.length}</p>
              </div>
              <div className="p-1.5 sm:p-2 rounded-lg bg-amber-50 dark:bg-amber-950">
                <CalendarX className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-interactive" data-testid="stat-card-inhouse">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">In-House</p>
                <p className="text-xl sm:text-2xl font-bold font-['Outfit'] text-purple-600">{inHouse}</p>
              </div>
              <div className="p-1.5 sm:p-2 rounded-lg bg-purple-50 dark:bg-purple-950">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {canSeeFinancials && (
          <Card className="card-interactive col-span-2 sm:col-span-1" data-testid="stat-card-profit">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{financialData.profit >= 0 ? 'Profit' : 'Loss'}</p>
                  <p className={`text-xl sm:text-2xl font-bold font-['Outfit'] ${financialData.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    €{Math.abs(financialData.profit).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5 hidden sm:block">
                    <p>Revenue: €{financialData.revenue.toLocaleString()}</p>
                    <p>Expenses: €{financialData.expenses.toLocaleString()}</p>
                  </div>
                </div>
                <div className={`p-1.5 sm:p-2 rounded-lg ${financialData.profit >= 0 ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-rose-50 dark:bg-rose-950'}`}>
                  {financialData.profit >= 0 ? <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-600" /> : <TrendingDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-rose-600" />}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Main Content Grid: Today's Arrivals + Room Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        
        {/* Today's Arrivals */}
        {widgets.todays_arrivals !== false && (
        <Card data-testid="todays-arrivals-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarCheck className="h-5 w-5 text-emerald-600" />
              Today's Arrivals
              <Badge variant="secondary" className="ml-auto">{todayArrivals.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayArrivals.length > 0 ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                {todayArrivals.map((res) => {
                  const guest = getGuestById(res.guest_id);
                  const roomNum = getRoomNumber(res.room_id);
                  const roomType = getRoomTypeName(res.room_id);
                  
                  return (
                    <div 
                      key={res.id} 
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      data-testid={`arrival-item-${res.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">Room {roomNum} • {roomType}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
                        onClick={() => openCheckInDialog(res)}
                        data-testid={`checkin-btn-${res.id}`}
                      >
                        <LogIn className="h-4 w-4 mr-1" />
                        Check-in
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarCheck className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No arrivals scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Room Status Grid */}
        {widgets.room_status !== false && (
        <Card data-testid="room-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BedDouble className="h-5 w-5" />
              Room Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Status Summary */}
            <div className={`grid gap-2 mb-4`} style={{ gridTemplateColumns: `repeat(${Math.min(visibleStatusConfigs.length, 6)}, minmax(0, 1fr))` }}>
              {visibleStatusConfigs.map(([status, config]) => (
                <div key={status} className={`text-center p-2 rounded-lg ${config.bgLight}`}>
                  <p className={`text-lg font-bold ${config.textColor}`}>{statusCounts[status] || 0}</p>
                  <p className={`text-xs ${config.textColor}`}>{config.label}</p>
                </div>
              ))}
            </div>
            
            {/* Room Grid */}
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-1.5 sm:gap-2 max-h-[280px] sm:max-h-[320px] overflow-y-auto">
              {rooms.map((room) => {
                const statusConfig = ROOM_STATUS_CONFIG[room.status] || ROOM_STATUS_CONFIG.available;
                const guestName = room.status === 'occupied' ? getGuestForRoom(room.id) : null;
                const roomNum = room.number || room.room_number;
                
                return (
                  <div
                    key={room.id}
                    className={`rounded-lg p-1.5 sm:p-2 flex flex-col items-center justify-center text-white cursor-pointer hover:opacity-90 hover:scale-105 transition-all min-h-[55px] sm:min-h-[70px] ${statusConfig.color}`}
                    title={guestName ? `Room ${roomNum} - ${guestName}` : `Room ${roomNum} - ${statusConfig.label}`}
                    data-testid={`room-tile-${roomNum}`}
                  >
                    <span className="text-sm sm:text-base font-bold">{roomNum}</span>
                    {guestName && (
                      <span className="text-[10px] sm:text-xs font-semibold truncate max-w-full text-center leading-tight mt-0.5 sm:mt-1 bg-black/20 px-1 sm:px-1.5 py-0.5 rounded">
                        {guestName.split(' ')[0]}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {rooms.length === 0 && (
              <p className="text-center text-muted-foreground py-8">No rooms configured yet</p>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* Secondary Row: Today's Departures + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        
        {/* Today's Departures */}
        {widgets.todays_departures !== false && (
        <Card data-testid="todays-departures-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <CalendarX className="h-5 w-5 text-amber-600" />
              Today's Departures
              <Badge variant="secondary" className="ml-auto">{todayDepartures.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {todayDepartures.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                {todayDepartures.map((res) => {
                  const guest = getGuestById(res.guest_id);
                  const roomNum = getRoomNumber(res.room_id);
                  
                  return (
                    <div 
                      key={res.id} 
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                      data-testid={`departure-item-${res.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center shrink-0">
                          <User className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">Room {roomNum}</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-amber-500 text-amber-600 hover:bg-amber-100 dark:border-amber-400 dark:text-amber-400 dark:hover:bg-amber-950 shrink-0"
                        onClick={() => handleCheckOut(res)}
                        data-testid={`checkout-btn-${res.id}`}
                      >
                        <CalendarX className="h-4 w-4 mr-1" />
                        Check-out
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <CalendarX className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No departures scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Tasks Card */}
        {widgets.tasks_overview !== false && (
        <Card data-testid="tasks-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Tasks Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-100 dark:bg-cyan-900">
                  <Sparkles className="h-4 w-4 text-cyan-600" />
                </div>
                <span className="text-sm font-medium">Pending Cleaning</span>
              </div>
              <Badge variant={pendingCleaning > 0 ? 'default' : 'secondary'}>{pendingCleaning}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900">
                  <Wrench className="h-4 w-4 text-orange-600" />
                </div>
                <span className="text-sm font-medium">Open Maintenance</span>
              </div>
              <Badge variant={openMaintenance > 0 ? 'default' : 'secondary'}>{openMaintenance}</Badge>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold">{rooms.length}</p>
                <p className="text-xs text-muted-foreground">Total Rooms</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                <p className="text-2xl font-bold text-emerald-600">{statusCounts.available}</p>
                <p className="text-xs text-emerald-600">Available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Profit/Loss Summary - Only for Admin/Manager/Accountant */}
      {canSeeFinancials && (
      <Card data-testid="profit-loss-summary-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            {financialData.profit >= 0
              ? <TrendingUp className="h-5 w-5 text-emerald-600" />
              : <TrendingDown className="h-5 w-5 text-rose-600" />}
            Profit / Loss Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950">
              <p className="text-xs font-medium text-blue-600 mb-1">Reservation Revenue</p>
              <p className="text-xl font-bold text-blue-700 font-['Outfit']">
                €{financialData.reservationRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950">
              <p className="text-xs font-medium text-purple-600 mb-1">Transfer Revenue</p>
              <p className="text-xl font-bold text-purple-700 font-['Outfit']">
                €{financialData.transferRevenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950">
              <p className="text-xs font-medium text-rose-600 mb-1">Total Expenses</p>
              <p className="text-xl font-bold text-rose-700 font-['Outfit']">
                €{financialData.expenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className={`p-4 rounded-lg ${financialData.profit >= 0 ? 'bg-emerald-50 dark:bg-emerald-950' : 'bg-red-50 dark:bg-red-950'}`}>
              <p className={`text-xs font-medium mb-1 ${financialData.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                Net {financialData.profit >= 0 ? 'Profit' : 'Loss'}
              </p>
              <p className={`text-xl font-bold font-['Outfit'] ${financialData.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                €{Math.abs(financialData.profit).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Check-in Dialog */}
      <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="checkin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-emerald-600" />
              Guest Check-in
            </DialogTitle>
            <DialogDescription>
              Review reservation details and confirm check-in
            </DialogDescription>
          </DialogHeader>
          
          {selectedReservation && (
            <div className="space-y-4">
              {/* Guest Info */}
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                    <User className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{getGuestName(selectedReservation.guest_id)}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedReservation.adults} adult{selectedReservation.adults !== 1 ? 's' : ''}
                      {selectedReservation.children > 0 && `, ${selectedReservation.children} child${selectedReservation.children !== 1 ? 'ren' : ''}`}
                    </p>
                  </div>
                </div>
                
                {/* Guest Contact */}
                {(() => {
                  const guest = getGuestById(selectedReservation.guest_id);
                  if (!guest) return null;
                  return (
                    <div className="space-y-1 text-sm">
                      {guest.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="h-3.5 w-3.5" />
                          <span>{guest.email}</span>
                        </div>
                      )}
                      {guest.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{guest.phone}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Reservation Details */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg border">
                  <p className="text-muted-foreground text-xs mb-1">Room</p>
                  <p className="font-semibold">{getRoomNumber(selectedReservation.room_id)}</p>
                  <p className="text-xs text-muted-foreground">{getRoomTypeName(selectedReservation.room_id)}</p>
                </div>
                <div className="p-3 rounded-lg border">
                  <p className="text-muted-foreground text-xs mb-1">Rate/Night</p>
                  <p className="font-semibold">€{selectedReservation.rate_per_night?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="p-3 rounded-lg border">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <CalendarCheck className="h-3 w-3" />
                    Check-in
                  </div>
                  <p className="font-semibold">{selectedReservation.check_in_date?.slice(0, 10)}</p>
                </div>
                <div className="p-3 rounded-lg border">
                  <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                    <CalendarX className="h-3 w-3" />
                    Check-out
                  </div>
                  <p className="font-semibold">{selectedReservation.check_out_date?.slice(0, 10)}</p>
                </div>
              </div>

              {/* Total */}
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-emerald-600" />
                    <span className="font-medium">Total Amount</span>
                  </div>
                  <span className="font-bold text-lg text-emerald-600">
                    €{(() => {
                      // Always recalculate: use date-only diff to avoid time-of-day errors
                      const rate = selectedReservation.rate_per_night || 0;
                      const checkIn = new Date(selectedReservation.check_in_date);
                      const checkOut = new Date(selectedReservation.check_out_date);
                      const ci = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate());
                      const co = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate());
                      const nights = Math.max(1, Math.round((co - ci) / 86400000));
                      const total = rate > 0 ? rate * nights : (selectedReservation.total_amount || 0);
                      return total.toFixed(2);
                    })()}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Invoice will be auto-generated on check-in</p>
              </div>

              {selectedReservation.notes && (
                <div className="p-3 rounded-lg border border-dashed">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm">{selectedReservation.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setCheckInDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCheckIn}
              disabled={checkingIn}
              data-testid="confirm-checkin-btn"
            >
              {checkingIn ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Confirm Check-in
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
